/* eslint-disable no-unused-expressions,no-nested-ternary */
import React, { useEffect, useState } from 'react'
import { Form, FormInstance, Input, Table } from 'antd'
import { BroadcastContact, MobileNumber } from 'types'
import handleAxiosError from 'utils/handleAxiosError'
import getBroadcastContacts from 'api/broadcast/getBroadcastContacts'
import getFullName, { LikeContactUser } from 'utils/getFullName'
import { BroadcastFormValues } from 'component/Broadcast/BroadcastDetails'
import { ColumnsType } from 'antd/lib/table'
import useSingleBroadcast from 'hooks/useSingleBroadcast'
import getMobileNumber from 'utils/getMobileNumber'

const { Item } = Form
const { Search } = Input

const size = 10

interface Props {
	form: FormInstance<BroadcastFormValues>
	canEditBroadcast: boolean
}

const BroadcastFormReview: React.FC<Props> = ({ form, canEditBroadcast }) => {
	const { broadcast } = useSingleBroadcast()

	const [records, setRecords] = useState<BroadcastContact[]>([])
	const [pagination, setPagination] = useState({
		totalCount: 0,
		totalPages: 1,
		currentPage: 1,
	})
	const [page, setPage] = useState(1)
	const [query, setQuery] = useState('')
	const [loading, setLoading] = useState(false)
	const [editingContact, setEditingContact] = useState<null | BroadcastContact>(
		null
	)

	useEffect(() => {
		const loadRecords = async () => {
			if (!broadcast) return

			try {
				setLoading(true)

				const options = {
					id: broadcast._id,
					page,
					size,
					query,
				}

				// eslint-disable-next-line no-shadow
				const { records, ...pagination } = await getBroadcastContacts(options)

				setRecords(records)
				setPagination(pagination)
			} catch (error: any) {
				handleAxiosError(error)
			} finally {
				setLoading(false)
			}
		}

		loadRecords()
	}, [page, form, broadcast, query])

	const updateHeaderParams = (index: number, newValue: string) => {
		setEditingContact(current =>
			current === null
				? null
				: {
						...current,
						header_params: current.header_params.map((param, i) =>
							i === index ? newValue : param
						),
				  }
		)
	}

	const updateParams = (index: number, newValue: string) => {
		setEditingContact(current =>
			current === null
				? null
				: {
						...current,
						params: current.params.map((param, i) =>
							i === index ? newValue : param
						),
				  }
		)
	}

	const updateUrlParams = (index: number, newValue: string) => {
		setEditingContact(current =>
			current === null
				? null
				: {
						...current,
						url_params: current.url_params.map((param, i) =>
							i === index ? newValue : param
						),
				  }
		)
	}

	const columns: ColumnsType<BroadcastContact> = [
		{
			title: 'Name',
			dataIndex: 'contact',
			show: true,
			render: (contact: LikeContactUser) => getFullName(contact),
		},
		{
			title: 'Mobile Number',
			dataIndex: ['contact', 'mobile'],
			show: true,
			render: (mobile: MobileNumber) => getMobileNumber(mobile),
		},
		...form.getFieldValue('templateHeaderParameter').map((parameter: any) => ({
			title: 'Header Param',
			dataIndex: ['header_params', parameter.number - 1],
			show: true,
			render: (value: string, record: BroadcastContact) =>
				editingContact?._id === record._id ? (
					<Item
						style={{ margin: 0 }}
						validateStatus={
							editingContact.header_params[parameter.number - 1] === '' ? 'error' : ''
						}
						help={
							editingContact.header_params[parameter.number - 1] === ''
								? 'Parameter Required'
								: null
						}
					>
						<Input
							value={editingContact.header_params[parameter.number - 1]}
							onChange={event =>
								updateHeaderParams(parameter.number - 1, event.target.value)
							}
							onKeyPress={event => {
								event.key === 'Enter' ? event.preventDefault() : null
							}}
							placeholder='Enter Value'
							disabled={!canEditBroadcast}
						/>
					</Item>
				) : (
					<span>{value}</span>
				),
		})),
		...form.getFieldValue('parameters').map((parameter: any) => ({
			title: `Field {{${parameter.number}}}`,
			dataIndex: ['params', parameter.number - 1],
			show: true,
			render: (value: string, record: BroadcastContact) =>
				editingContact?._id === record._id ? (
					<Item
						style={{ margin: 0 }}
						validateStatus={
							editingContact.params[parameter.number - 1] === '' ? 'error' : ''
						}
						help={
							editingContact.params[parameter.number - 1] === ''
								? 'Parameter Required'
								: null
						}
					>
						<Input
							value={editingContact.params[parameter.number - 1]}
							onChange={event =>
								updateParams(parameter.number - 1, event.target.value)
							}
							onKeyPress={event => {
								event.key === 'Enter' ? event.preventDefault() : null
							}}
							placeholder='Enter Value'
							disabled={!canEditBroadcast}
						/>
					</Item>
				) : (
					<span>{value}</span>
				),
		})),
		...form.getFieldValue('templateUrlParameter').map((parameter: any) => ({
			title: 'URL Param',
			dataIndex: ['url_params', parameter.number - 1],
			show: true,
			render: (value: string, record: BroadcastContact) =>
				editingContact?._id === record._id ? (
					<Item
						style={{ margin: 0 }}
						validateStatus={
							editingContact.header_params[parameter.number - 1] === '' ? 'error' : ''
						}
						help={
							editingContact.header_params[parameter.number - 1] === ''
								? 'Parameter Required'
								: null
						}
					>
						<Input
							value={editingContact.url_params[parameter.number - 1]}
							onChange={event =>
								updateUrlParams(parameter.number - 1, event.target.value)
							}
							onKeyPress={event => {
								event.key === 'Enter' ? event.preventDefault() : null
							}}
							placeholder='Enter Value'
							disabled={!canEditBroadcast}
						/>
					</Item>
				) : (
					<span>{value}</span>
				),
		})),
	]

	return (
		<>
			<Search
				placeholder='Search Audience by Name or Mobile Number'
				allowClear
				onSearch={value => {
					setQuery(value)
				}}
				onKeyPress={event =>
					event.key === 'Enter' ? event.preventDefault() : null
				}
			/>
			<div
				style={{
					position: 'relative',
				}}
			>
				<Table
					dataSource={records}
					columns={columns}
					loading={loading}
					pagination={{
						current: pagination.currentPage,
						total: pagination.totalCount,
						pageSize: size,
						onChange(p) {
							setPage(p)
						},
					}}
				/>
			</div>
		</>
	)
}

export default BroadcastFormReview
